/**
 * Global Notification System
 * Modern, elegant, and efficient notification provider for the entire app
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react'

import { BaseComponentProps } from '@/shared/types/common.types'
import type { NotificationType } from '@/shared/types/ui.types'
import { UI_TIMINGS } from '@/shared/utils/constants'
import { logger } from '@/shared/utils/logger'

import { NotificationContainer } from './NotificationContainer'

export interface AppNotification {
  id: string
  type: NotificationType
  message: string
  title?: string
  duration?: number
  dismissible?: boolean
  /**
   * Optional rich content rendered BELOW `message`. Lets callers ship
   * multi-line or interactive payloads (links, inline actions) without
   * forking the toast surface. Plain string toasts ignore this.
   *
   * Added for issue #322 B.3 (post-sync resumen toast).
   */
  content?: React.ReactNode
  /**
   * Optional single action rendered as a button under the message (e.g.
   * "Reload"). Running it dismisses the notification. The label is already
   * translated by the caller.
   */
  action?: { label: string; onClick: () => void }
  /**
   * Notifications sharing a live `dedupeKey` collapse into ONE: the existing
   * entry keeps its id, takes the new content and restarts its timer instead
   * of stacking a second copy.
   */
  dedupeKey?: string
  /**
   * Bumped by the provider each time a deduplicated call refreshes this entry;
   * `NotificationItem` restarts its timers when it changes. Callers never set it.
   */
  version?: number
}

/** Default auto-dismiss budget per type: ONE policy, read from `UI_TIMINGS`. */
const DEFAULT_DURATION_BY_TYPE: Record<NotificationType, number> = {
  success: UI_TIMINGS.TOAST_SUCCESS,
  info: UI_TIMINGS.TOAST_INFO,
  warning: UI_TIMINGS.TOAST_WARNING,
  error: UI_TIMINGS.TOAST_ERROR,
}

interface NotificationContextType {
  // Simple API - Most common use case
  notify: (message: string, type?: NotificationType, duration?: number) => string

  // Specific type methods
  success: (message: string, duration?: number) => string
  error: (message: string, duration?: number) => string
  warning: (message: string, duration?: number) => string
  info: (message: string, duration?: number) => string

  // Alias methods for compatibility (used by PasswordGenerator)
  showSuccess: (message: string, duration?: number) => string
  showError: (message: string, duration?: number) => string

  // Advanced API - For complex cases
  show: (notification: Omit<AppNotification, 'id'>) => string

  // Control methods
  dismiss: (id: string) => void
  clear: () => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

interface NotificationProviderProps extends BaseComponentProps {
  children: React.ReactNode
  maxNotifications?: number
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  /** Overrides the per-type default duration (tests); production leaves it unset. */
  defaultDuration?: number
}

/**
 * DISMISSAL POLICY (issue: error toasts never went away).
 *
 * `error()` defaulted to `duration = 0`, which `NotificationItem` reads as
 * "never auto-dismiss". Two of them were observed alive after 30+ minutes,
 * stacked over the sources table and covering the fixed bottom-right import
 * progress panel. An error the user cannot make disappear is not a stronger
 * signal, it is furniture.
 *
 * Errors expire by default (as long as warnings, longer than success/info), reusing
 * `UI_TIMINGS.TOAST_ERROR` — the same budget the react-hot-toast surface
 * mounted alongside this one already uses, so the two agree. The toast stays
 * manually dismissible, and hovering its close button still pauses the timer
 * (NotificationItem), which covers "I need more time to read this".
 */
const ERROR_TOAST_DURATION_MS: number = UI_TIMINGS.TOAST_ERROR

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  maxNotifications = 4,
  // PLACEMENT: top-right, not bottom-right. `ImportProgressIndicator` is a
  // `fixed bottom-4 right-4` panel and this container is `fixed bottom-4
  // right-4 z-[9999]` — the exact same coordinates, one z-layer above. Toasts
  // therefore covered the live import progress the user was watching. Moving to
  // the top-right also matches the `<Toaster position="top-right">` mounted in
  // AppProviders, so the app has ONE toast corner instead of two.
  position = 'top-right',
  defaultDuration,
  testId = 'notification-provider',
}) => {
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  // Synchronous mirror of `notifications`: `show` must know at call time whether
  // a live entry shares the `dedupeKey` so it can return that entry's id.
  const notificationsRef = useRef<AppNotification[]>([])

  const commit = useCallback((next: AppNotification[]) => {
    notificationsRef.current = next
    setNotifications(next)
  }, [])

  const generateId = useCallback(() => {
    return `notif-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  }, [])

  const show = useCallback(
    (notification: Omit<AppNotification, 'id'>) => {
      // Resolve the duration explicitly: spreading `notification` last would let
      // an EXPLICIT `undefined` duration (e.g. `notify(msg, 'info')` forwards
      // `duration: undefined`) overwrite the default, and `NotificationItem`
      // treats a non-positive duration as "stay forever".
      // `duration: 0` (persistent) is only legal on an error: anything else
      // falls back to its type budget so no success/info/warning can linger.
      const typeDefault = defaultDuration ?? DEFAULT_DURATION_BY_TYPE[notification.type]
      let duration = typeDefault
      if (typeof notification.duration === 'number') {
        if (notification.duration === 0 && notification.type !== 'error') {
          logger.warn('notification.persistent-not-allowed', { type: notification.type })
        } else {
          duration = notification.duration
        }
      }

      const current = notificationsRef.current
      const { dedupeKey } = notification
      // A persistent error is never replaced by a non-error sharing its key:
      // a later success/info must not silently swallow an unresolved failure.
      const existing = dedupeKey
        ? current.find(
            (n) =>
              n.dedupeKey === dedupeKey &&
              !(n.type === 'error' && n.duration === 0 && notification.type !== 'error'),
          )
        : undefined

      if (existing) {
        // A persistent error stays persistent when a later error shares its key:
        // only the text refreshes, so deduplication never expires an unresolved failure.
        const keepsPersistent =
          existing.type === 'error' && existing.duration === 0 && notification.type === 'error'
        const refreshed: AppNotification = {
          dismissible: true,
          ...notification,
          id: existing.id,
          duration: keepsPersistent ? 0 : duration,
          version: (existing.version ?? 0) + 1,
        }
        commit(current.map((n) => (n.id === existing.id ? refreshed : n)))
        return existing.id
      }

      const id = generateId()
      const newNotification: AppNotification = {
        dismissible: true,
        ...notification,
        id,
        duration,
      }
      // Keep only the latest notifications to prevent overflow
      commit([...current, newNotification].slice(-maxNotifications))

      return id
    },
    [generateId, maxNotifications, defaultDuration, commit],
  )

  // Simple API
  const notify = useCallback(
    (message: string, type: NotificationType = 'info', duration?: number) => {
      return show({ message, type, duration })
    },
    [show],
  )

  // Specific type methods with smart defaults
  const success = useCallback(
    (message: string, duration: number = UI_TIMINGS.TOAST_SUCCESS) => {
      return show({ message, type: 'success', duration })
    },
    [show],
  )

  const error = useCallback(
    (message: string, duration = ERROR_TOAST_DURATION_MS) => {
      // Finite by default — see ERROR_TOAST_DURATION_MS. Only an explicit `duration: 0` is persistent.
      return show({ message, type: 'error', duration })
    },
    [show],
  )

  const warning = useCallback(
    (message: string, duration: number = UI_TIMINGS.TOAST_WARNING) => {
      return show({ message, type: 'warning', duration })
    },
    [show],
  )

  const info = useCallback(
    (message: string, duration: number = UI_TIMINGS.TOAST_INFO) => {
      return show({ message, type: 'info', duration })
    },
    [show],
  )

  const dismiss = useCallback(
    (id: string) => {
      commit(notificationsRef.current.filter((notif) => notif.id !== id))
    },
    [commit],
  )

  const clear = useCallback(() => {
    commit([])
  }, [commit])

  const contextValue: NotificationContextType = {
    notify,
    success,
    error,
    warning,
    info,
    showSuccess: success, // Alias for compatibility
    showError: error, // Alias for compatibility
    show,
    dismiss,
    clear,
  }

  return (
    <div data-testid={testId}>
      <NotificationContext.Provider value={contextValue}>
        {children}
        <NotificationContainer
          notifications={notifications}
          position={position}
          onDismiss={dismiss}
        />
      </NotificationContext.Provider>
    </div>
  )
}

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}
